const clamp = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(maximum, value));

const finite = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const getCoalescedPointerEvents = (event) => {
  const nativeEvent = event?.nativeEvent || event;
  try {
    const samples = nativeEvent?.getCoalescedEvents?.();
    if (Array.isArray(samples) && samples.length) return samples;
  } catch {
    // Some WebViews expose getCoalescedEvents but throw while a pointer is
    // being cancelled. The final browser event is still a valid sample.
  }
  return nativeEvent ? [nativeEvent] : [];
};

/**
 * Convert a browser pointer position to the blackboard viewBox coordinate
 * system. SVG's screen CTM is the authoritative transform: it remains
 * correct when the board is CSS-zoomed, scrolled, centred with an SVG
 * preserveAspectRatio gutter, or rendered on a high-DPI screen. Older
 * WebViews without SVGPoint/CTM support use the dimension-safe fallback.
 */
export const projectPointerToBoard = (
  event,
  svg,
  boardWidth = 1600,
  boardHeight = 900
) => {
  const widthLimit = Math.max(1, finite(boardWidth, 1600));
  const heightLimit = Math.max(1, finite(boardHeight, 900));
  const clientX = finite(event?.clientX);
  const clientY = finite(event?.clientY);

  try {
    const matrix = svg?.getScreenCTM?.();
    const inverse = matrix?.inverse?.();
    const point = svg?.createSVGPoint?.();
    if (inverse && point) {
      point.x = clientX;
      point.y = clientY;
      const transformed = point.matrixTransform(inverse);
      if (Number.isFinite(transformed?.x) && Number.isFinite(transformed?.y)) {
        return {
          x: clamp(transformed.x, 0, widthLimit),
          y: clamp(transformed.y, 0, heightLimit),
        };
      }
    }
  } catch {
    // Detached SVG nodes and some embedded WebViews may expose an incomplete
    // CTM. The rect fallback below still handles the common responsive case.
  }

  const rect = svg?.getBoundingClientRect?.() || {};
  const renderedWidth = Math.max(1, finite(rect.width, widthLimit));
  const renderedHeight = Math.max(1, finite(rect.height, heightLimit));
  return {
    x: clamp(
      ((clientX - finite(rect.left)) / renderedWidth) * widthLimit,
      0,
      widthLimit
    ),
    y: clamp(
      ((clientY - finite(rect.top)) / renderedHeight) * heightLimit,
      0,
      heightLimit
    ),
  };
};

/**
 * Keep roughly half a physical pixel between stored samples. This prevents
 * very dense stylus streams from making huge snapshots while retaining fine
 * handwriting on phones, zoomed boards and high-DPI pen displays.
 */
export const getBoardSampleSpacing = (
  svg,
  boardWidth = 1600,
  devicePixelRatio = globalThis.devicePixelRatio || 1
) => {
  const renderedWidth = Math.max(
    1,
    finite(svg?.getBoundingClientRect?.()?.width, boardWidth)
  );
  const density = clamp(finite(devicePixelRatio, 1), 1, 4);
  const boardUnitsPerCssPixel = Math.max(0.01, finite(boardWidth, 1600) / renderedWidth);
  return clamp((boardUnitsPerCssPixel * 0.75) / density, 0.3, 3);
};

export const snapshotPointerSamples = (events = []) => (events || []).map((event) => ({
  clientX: finite(event?.clientX),
  clientY: finite(event?.clientY),
  pressure: finite(event?.pressure),
  pointerType: String(event?.pointerType || 'mouse'),
}));

export const normalizePointerPressure = (event) => {
  const pointerType = String(event?.pointerType || 'mouse').toLowerCase();
  const pressure = finite(event?.pressure, 0);
  if (pointerType !== 'pen' || pressure <= 0) return 0.5;
  return clamp(pressure, 0.05, 1);
};

export const appendStrokeSamples = (
  existingPoints,
  events,
  project,
  minimumDistance = 0.8
) => {
  const next = [...(existingPoints || [])];
  for (const event of events || []) {
    const projected = project(event);
    if (!projected) continue;
    const point = {
      x: finite(projected.x),
      y: finite(projected.y),
      pressure: normalizePointerPressure(event),
      pointerType: String(event?.pointerType || 'mouse'),
    };
    const previous = next[next.length - 1];
    if (
      previous &&
      Math.hypot(point.x - previous.x, point.y - previous.y) < minimumDistance
    ) {
      // Preserve the newest pressure reading without growing the payload with
      // hundreds of duplicate samples from high-frequency styluses.
      next[next.length - 1] = { ...previous, pressure: point.pressure };
    } else {
      next.push(point);
    }
  }
  return next;
};

const coordinate = (point) =>
  `${finite(point?.x).toFixed(1)} ${finite(point?.y).toFixed(1)}`;

const midpoint = (left, right) => ({
  x: (finite(left?.x) + finite(right?.x)) / 2,
  y: (finite(left?.y) + finite(right?.y)) / 2,
});

export const buildSmoothStrokePath = (points = []) => {
  if (!points.length) return '';
  if (points.length === 1) {
    const point = points[0];
    return `M ${coordinate(point)} L ${(finite(point.x) + 0.01).toFixed(2)} ${finite(point.y).toFixed(1)}`;
  }

  let path = `M ${coordinate(points[0])}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const end = midpoint(points[index], points[index + 1]);
    path += ` Q ${coordinate(points[index])} ${coordinate(end)}`;
  }
  path += ` L ${coordinate(points[points.length - 1])}`;
  return path;
};

export const pressureStrokeWidth = (baseWidth, pressure) => {
  const base = Math.max(0.5, finite(baseWidth, 1));
  const normalized = clamp(finite(pressure, 0.5), 0.05, 1);
  return clamp(base * (0.35 + normalized * 1.3), base * 0.3, base * 1.7);
};

export const buildPressureStrokeSegments = (points = [], baseWidth = 1) => {
  if (points.length < 2) return [];
  let start = points[0];
  return points.slice(1).map((control, offset) => {
    const index = offset + 1;
    const nextPoint = points[index + 1];
    const end = nextPoint ? midpoint(control, nextPoint) : control;
    const pressure = (
      finite(points[index - 1]?.pressure, 0.5) +
      finite(control?.pressure, 0.5)
    ) / 2;
    const segment = {
      key: `${index}-${coordinate(end)}`,
      d: `M ${coordinate(start)} Q ${coordinate(control)} ${coordinate(end)}`,
      strokeWidth: pressureStrokeWidth(baseWidth, pressure),
    };
    start = end;
    return segment;
  });
};

// Keep pressure rendering bounded as a page grows. Each bucket contains
// separate M/Q subpaths, preserving every sampled curve without thousands of
// React/SVG nodes per handwritten word.
export const buildPressureStrokePaths = (points = [], baseWidth = 1) => {
  const buckets = new Map();
  buildPressureStrokeSegments(points, baseWidth).forEach((segment) => {
    const ratio = segment.strokeWidth / Math.max(0.5, finite(baseWidth, 1));
    const bucket = Math.round(clamp(ratio, 0.35, 1.7) * 8);
    if (!buckets.has(bucket)) buckets.set(bucket, { key: `pressure-${bucket}`, d: '', strokeWidth: Math.max(0.5, finite(baseWidth, 1)) * bucket / 8 });
    buckets.get(bucket).d += `${segment.d} `;
  });
  return [...buckets.values()];
};

export const getPointerReleaseSamples = (event) => {
  const native = event?.nativeEvent || event;
  if (native?.type !== 'pointerup') return [];
  const x = Number(native.clientX); const y = Number(native.clientY);
  if (!Number.isFinite(x) || !Number.isFinite(y) || (x === 0 && y === 0)) return [];
  return getCoalescedPointerEvents(event);
};
